/**
 * CRM Company Detail — 360° view with edit, interaction creation, skeleton loading.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, EmptyState, Button, Skeleton, SkeletonCard } from '../../components/ui';
import { DetailHeader, DetailSection, FieldGrid, Field } from '../../components/detail';
import { Markdown } from '../../components/markdown';
import { Building2, Globe, Mail, Users, Clock, Edit3, Plus, Calendar, CheckSquare } from '../../lib/icons';
import { useT } from '../../lib/i18n';
import { formatDate, timeAgo, safeParse } from '../../lib/utils';
import { fetchCompanyDetail } from '../../lib/crm-api';
import type { CrmCompany, CrmContact, CrmDeal, CrmFollowup, CrmInteraction, CrmActivity } from '../../lib/crm-api';
import { CompanyFormDialog, ContactFormDialog, InteractionFormDialog, FollowupFormDialog } from './forms';
import { DealsList } from './deals-list';
import { DriveBrowser } from '../../components/drive/drive-browser';

interface Props {
  companyId: number;
}

export function CompanyDetailPage({ companyId }: Props) {
  const t = useT();
  const [company, setCompany] = useState<CrmCompany | null>(null);
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [deals, setDeals] = useState<CrmDeal[]>([]);
  const [followups, setFollowups] = useState<CrmFollowup[]>([]);
  const [interactions, setInteractions] = useState<CrmInteraction[]>([]);
  const [_activities, setActivities] = useState<CrmActivity[]>([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [editCompanyOpen, setEditCompanyOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [addInteractionOpen, setAddInteractionOpen] = useState(false);
  const [addFollowupOpen, setAddFollowupOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCompanyDetail(companyId);
      setCompany(data.company);
      setContacts(data.contacts);
      setDeals(data.deals);
      setFollowups(data.followups || []);
      setInteractions(data.interactions);
      setActivities(data.activities);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          <Skeleton className="h-4 w-32" />
          <div className="flex gap-4">
            <Skeleton className="h-12 w-12 rounded-xl" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      </div>
    );
  }

  if (!company) {
    return <EmptyState icon={Building2} title={t('crm.companyNotFound')} description={t('crm.companyNotFoundDesc')} />;
  }

  const tags = safeParse<string[]>(company.tags, []);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <DetailHeader
          onBack={() => (window.location.hash = '#/crm/companies')}
          backLabel={t('crm.backToCompanies')}
          icon={
            <div className="w-12 h-12 rounded-xl bg-surface-muted flex items-center justify-center flex-shrink-0">
              <Building2 size={22} className="text-fg-muted" />
            </div>
          }
          titlePrefix={
            company.customer_no ? <span className="text-xs font-mono text-fg-muted">{company.customer_no}</span> : null
          }
          title={company.name}
          titleSuffix={
            <button
              onClick={() => setEditCompanyOpen(true)}
              className="p-1 rounded hover:bg-surface-muted text-fg-faint hover:text-fg-secondary transition-colors"
              title={t('crm.editCompany')}
            >
              <Edit3 size={14} />
            </button>
          }
          subtitle={
            <>
              {company.domain && (
                <span className="text-sm text-fg-secondary flex items-center gap-1">
                  <Globe size={12} className="text-fg-faint" />
                  {company.domain}
                </span>
              )}
              {company.country && (
                <span className="text-sm text-fg-muted">
                  {[company.city, company.region, company.country].filter(Boolean).join(', ')}
                </span>
              )}
              {company.status && <Badge variant="warning">{company.status}</Badge>}
              {company.agent_level && <Badge>{company.agent_level}</Badge>}
              {company.industry && <Badge>{company.industry}</Badge>}
              {company.source && <Badge variant="secondary">{company.source.replace(/_/g, ' ')}</Badge>}
            </>
          }
          badges={tags.length > 0 ? tags.map((tag) => <Badge key={tag}>{tag}</Badge>) : null}
        />

        {/* Details */}
        <DetailSection title="Details">
          <FieldGrid cols={3}>
            <Field label={t('crm.quoteLevel')} value={company.quote_level} hideEmpty />
            <Field label={t('crm.website')} value={company.website} hideEmpty />
            <Field label={t('crm.phone')} value={company.phone} hideEmpty />
            <Field label={t('crm.address')} value={company.address} hideEmpty />
            <Field
              label={t('crm.registeredAt')}
              value={company.registered_at ? formatDate(company.registered_at) : null}
              hideEmpty
            />
            <Field
              label={t('crm.nextFollowup')}
              value={company.next_followup_at ? formatDate(company.next_followup_at) : null}
              hideEmpty
            />
            <Field label={t('crm.createdAt')} value={formatDate(company.created_at)} hideEmpty />
            {company.notes && <Field label={t('crm.notes')} value={company.notes} span="full" />}
          </FieldGrid>
        </DetailSection>

        {/* Consignee / 收货信息 */}
        {(company.consignee_name || company.consignee_company || company.consignee_address) && (
          <DetailSection title={t('crm.consignee')}>
            <FieldGrid cols={3}>
              <Field label={t('crm.company')} value={company.consignee_company} hideEmpty />
              <Field label={t('crm.contact')} value={company.consignee_name} hideEmpty />
              <Field label={t('crm.email')} value={company.consignee_email} hideEmpty />
              <Field label={t('crm.phone')} value={company.consignee_phone} hideEmpty />
              <Field label={t('crm.address')} value={company.consignee_address} hideEmpty />
            </FieldGrid>
          </DetailSection>
        )}

        {company.ai_summary && (
          <DetailSection title="AI Summary">
            <div className="text-sm text-fg-secondary">
              <Markdown content={company.ai_summary} compact />
            </div>
          </DetailSection>
        )}

        {/* Files (云盘) — per-customer file cabinet */}
        <DetailSection title={t('crm.files')}>
          <DriveBrowser owner={{ scope: 'crm', company_id: companyId }} />
        </DetailSection>

        {/* Related records — each section stands on its own (no tabs) */}
        <DetailSection
          title={`${t('crm.contacts')} (${contacts.length})`}
          action={
            <Button size="sm" variant="ghost" onClick={() => setAddContactOpen(true)}>
              <Plus size={12} />
              <span className="ml-1">Add Contact</span>
            </Button>
          }
        >
          <ContactsTab contacts={contacts} />
        </DetailSection>

        <DetailSection
          title={`${t('crm.followups')} (${followups.length})`}
          action={
            <Button size="sm" variant="ghost" onClick={() => setAddFollowupOpen(true)}>
              <Plus size={12} />
              <span className="ml-1">{t('crm.logFollowup')}</span>
            </Button>
          }
        >
          <FollowupsTab followups={followups} />
        </DetailSection>

        <DetailSection title={`${t('crm.deals')} (${deals.length})`}>
          <DealsList deals={deals} emptyDescription="Create quotes or orders for this company" />
        </DetailSection>

        <DetailSection
          title={`${t('crm.interaction')} (${interactions.length})`}
          action={
            contacts.length > 0 ? (
              <Button size="sm" variant="ghost" onClick={() => setAddInteractionOpen(true)}>
                <Plus size={12} />
                <span className="ml-1">Log Interaction</span>
              </Button>
            ) : undefined
          }
        >
          <InteractionsTab interactions={interactions} />
        </DetailSection>
      </div>

      {/* Dialogs */}
      <CompanyFormDialog
        open={editCompanyOpen}
        onClose={() => setEditCompanyOpen(false)}
        onSaved={loadData}
        initial={company}
      />
      <ContactFormDialog
        open={addContactOpen}
        onClose={() => setAddContactOpen(false)}
        onSaved={loadData}
        defaultCompanyId={companyId}
      />
      <FollowupFormDialog
        open={addFollowupOpen}
        onClose={() => setAddFollowupOpen(false)}
        onSaved={loadData}
        companyId={companyId}
        contacts={contacts}
      />
      {contacts.length > 0 && (
        <InteractionFormDialog
          open={addInteractionOpen}
          onClose={() => setAddInteractionOpen(false)}
          onSaved={loadData}
          contactId={contacts[0].id}
          companyId={companyId}
        />
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────

function ContactsTab({ contacts }: { contacts: CrmContact[] }) {
  if (contacts.length === 0)
    return <EmptyState icon={Users} title="No contacts" description="Add contacts linked to this company" />;
  return (
    <div className="space-y-2">
      {contacts.map((c) => (
        <Card
          key={c.id}
          className="p-3 cursor-pointer hover:bg-surface-muted transition-colors"
          onClick={() => (window.location.hash = `#/crm/contacts/${c.id}`)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-surface-muted flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-fg-muted">{c.name.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-fg truncate">{c.name}</div>
              <div className="text-xs text-fg-muted truncate">
                {c.email} {c.title ? `· ${c.title}` : ''}
              </div>
            </div>
            {c.last_contacted_at && (
              <span className="text-[10px] text-fg-faint flex-shrink-0">{timeAgo(c.last_contacted_at)}</span>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

function FollowupsTab({ followups }: { followups: CrmFollowup[] }) {
  if (followups.length === 0)
    return <EmptyState icon={Calendar} title="No follow-ups" description="Sales follow-ups will appear here" />;
  return (
    <div className="space-y-2">
      {followups.map((f) => (
        <Card key={f.id} className="p-3">
          <div className="flex items-start gap-2">
            <Calendar size={13} className="text-warning mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {f.purpose && <span className="text-sm font-medium text-fg">{f.purpose}</span>}
                {f.outcome && <Badge variant="secondary">{f.outcome}</Badge>}
              </div>
              {f.result && <p className="text-[11px] text-fg-muted mt-1 line-clamp-3">{f.result}</p>}
              {f.next_goal && (
                <div className="text-[11px] text-fg-muted mt-1 flex items-center gap-1">
                  <CheckSquare size={11} className="text-fg-faint" />
                  {f.next_goal}
                  {f.next_followup_at && <span className="text-fg-faint"> · {formatDate(f.next_followup_at)}</span>}
                </div>
              )}
            </div>
            <span className="text-[10px] text-fg-faint flex-shrink-0">{timeAgo(f.occurred_at)}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}

function InteractionsTab({ interactions }: { interactions: CrmInteraction[] }) {
  if (interactions.length === 0)
    return <EmptyState icon={Mail} title="No interactions" description="Interactions will appear here" />;
  return (
    <div className="space-y-2">
      {interactions.map((i) => (
        <Card key={i.id} className="p-3">
          <div className="flex items-start gap-2">
            <div className="mt-0.5">
              {i.type.startsWith('email') ? (
                <Mail size={13} className="text-info" />
              ) : (
                <Clock size={13} className="text-fg-muted" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Badge variant={i.type === 'email_inbound' ? 'secondary' : 'default'}>
                  {i.type.replace(/_/g, ' ')}
                </Badge>
                {i.subject && <span className="text-xs text-fg-secondary truncate">{i.subject}</span>}
              </div>
              {i.content_preview && <p className="text-[11px] text-fg-muted mt-1 line-clamp-2">{i.content_preview}</p>}
            </div>
            <span className="text-[10px] text-fg-faint flex-shrink-0">{timeAgo(i.occurred_at)}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}
